import { type NormalizedDrawing, associateDrawing } from "@repo/api";
import { Loading, Text } from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  DebouncedSearchField,
  ModalSheet,
  SectionContainer,
  useToast,
} from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { useSearchDesign } from "../../hooks/search";
import { PlannerResultList } from "./PlannerResultList";

interface AddDrawingModalProps {
  isOpen: boolean;
  opportunityId: string;
  closeModal: () => void;
  existingDrawingIds: string[];
  filterAssociated?: boolean;
  customer?: Customer;
}

export const AddDrawingModal = ({
  isOpen,
  closeModal,
  opportunityId,
  existingDrawingIds,
  filterAssociated,
  customer,
}: AddDrawingModalProps) => {
  const { updateToast } = useToast();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const { data: searchResult, isValidating } = useSearchDesign(
    searchValue,
    customer
  );

  const [selectedDrawings, setSelectedDrawings] = useState<NormalizedDrawing[]>(
    []
  );

  const reset = () => {
    setSelectedDrawings([]);
    setSearchValue(undefined);
  };

  const associate = async () => {
    try {
      if (selectedDrawings.length > 0) {
        await Promise.all(
          selectedDrawings.map((drawing) =>
            associateDrawing(opportunityId, drawing.id, drawing.applicationId)
          )
        );
      }

      updateToast({
        id: selectedDrawings.map((d) => d.id).join(","),
        title: t("prompt.drawing_associated"),
      });
    } catch (err) {
      updateToast({
        id: selectedDrawings.map((d) => d.id).join(","),
        title: t("prompt.drawing_associated_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    }
  };

  const selectedDrawingIds = existingDrawingIds.concat(
    selectedDrawings.map((d) => d.id)
  );
  const onSelect = (selected: boolean, data: NormalizedDrawing) => {
    if (selected) {
      setSelectedDrawings([...selectedDrawings, data]);
      trackAnalytics("drawing_selected");
    } else {
      setSelectedDrawings(selectedDrawings.filter((p) => p.id !== data.id));
    }
  };

  return (
    <ModalSheet
      title={t("action.add_planner_drawings")}
      visible={isOpen}
      onClose={() => {
        reset();
        closeModal();
      }}
      primaryButtonProps={{
        text: t("action.add_to_project"),
        disabled: selectedDrawings.length === 0,
        onClick: () => {
          associate();
          reset();
          closeModal();
          trackAnalytics("add_to_project");
        },
      }}
    >
      <SectionContainer>
        <DebouncedSearchField
          id="seach-planner-input"
          placeholder={t("prompt.placeholder_search_planner")}
          debounceTime={700}
          onChange={setSearchValue}
        />
        {isValidating && <Loading text={t("prompt.loading")} />}
        {!isValidating &&
          (!searchResult || searchResult?.plannerList?.length === 0) &&
          searchValue && (
            <div>
              <Text>
                <T translationKey="prompt.no_drawings_found" />
              </Text>
            </div>
          )}
        {searchResult && (
          <SectionContainer>
            <PlannerResultList
              data-testid="planner-drawing-result-list"
              searchResult={searchResult}
              addedDrawingIds={existingDrawingIds}
              selectedDrawingIds={selectedDrawingIds}
              onSelect={onSelect}
              filterAssociated={filterAssociated}
            />
          </SectionContainer>
        )}
      </SectionContainer>
    </ModalSheet>
  );
};
